// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_DATA_URL;
const API_ENDPOINT = '/api/Document/workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_DATA_SCOPE;

const HEADERS = { 'Access-Control-Allow-Origin': '*' };

// ============= Types =============

export interface DownloadDocumentProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  documentId: string;
  type?: 'ORIGINAL' | 'CONVERSION';
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Downloads a document from a Data Room
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace identifier
 * @param documentId Document identifier
 * @param type Document type - ORIGINAL or CONVERSION (default: CONVERSION)
 * @returns Promise with blob data or error
 */
export const downloadDocumentBytes = async ({
  getAccessToken,
  workspaceId,
  documentId,
  type = 'CONVERSION',
}: DownloadDocumentProps): Promise<Blob> => {
  try {
    console.log(`🔄 Downloading document (${type})...`);

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/document/${documentId}/${type}/bytes`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'GET',
      headers: createHeaders(token),
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    console.log(`✅ Document downloaded successfully`);

    return blob;
  } catch (error) {
    console.error('🔴 Error downloading document:', error);
    throw error;
  }
};
